import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ListenerRole } from '../../../../hooks/usePeers';
import { useUpdateListenerProfile, useUpdateUser } from '../../../../hooks/useUsers';
import { SystemMessage, useSystemMessages } from '../../../../hooks/useComms';
import Modal from '../../../../components/common/modal';
import { toast } from 'react-toastify';
import useAuth from '../../../../hooks/useAuth';
import ImageCropEditor from './image-crop-editor';
import { cropImageWithPixels } from '../../../../utils/imageUtils';
import { validateImageFile } from '../../../../utils/fileValidation';
import { getAdminProfilePhotoState } from '../../../../utils/profilePhoto';
import { Area } from 'react-easy-crop';

interface RejectionModalData {
  templateId: number | null;
  message: string;
  sendMessage: boolean;
}
interface AvatarReviewProps {
  peerData: ListenerRole;
  refetchPeerData: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const AvatarReview: React.FC<AvatarReviewProps> = ({ peerData, refetchPeerData, onDirtyChange }) => {
  const photoState = getAdminProfilePhotoState(peerData);
  const initialStatus = photoState === 'rejected' ? 'rejected' : photoState === 'approved' ? 'approved' : 'review';
  const [selectedStatus, setSelectedStatus] = useState<string>(initialStatus);
  const savedStatusRef = useRef(initialStatus);

  useEffect(() => {
    onDirtyChange?.(selectedStatus !== savedStatusRef.current);
  }, [selectedStatus, onDirtyChange]);

  const [isDownloadingCurrent, setIsDownloadingCurrent] = useState(false);
  const [isDownloadingProposed, setIsDownloadingProposed] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [isCropEditorOpen, setIsCropEditorOpen] = useState(false);

  const [messagePopUp, setMessagePopUp] = useState(false);
  const [rejectionModalData, setRejectionModalData] = useState<RejectionModalData>({
    templateId: null,
    message: '',
    sendMessage: true,
  });
  const { mutate: updateListenerProfile, isLoading } = useUpdateListenerProfile();
  const { updateUser } = useUpdateUser();
  const { data: systemMessages } = useSystemMessages();
  const { authToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentPhotoUrl = peerData.current_profile_photo?.square_url || peerData.current_profile_photo?.file_url;
  const proposedPhotoUrl =
    peerData.proposed_profile_photo?.square_url || peerData.proposed_profile_photo?.file_url || null;

  const currentPhotoUrlWithCache = useMemo(
    () => (currentPhotoUrl ? `${currentPhotoUrl}?t=${Date.now()}` : null),
    [currentPhotoUrl],
  );
  const proposedPhotoUrlWithCache = useMemo(
    () => (proposedPhotoUrl ? `${proposedPhotoUrl}?t=${Date.now()}` : null),
    [proposedPhotoUrl],
  );

  const avatarRejectedMessages = useMemo(
    () => systemMessages?.filter((message: SystemMessage) => message.group?.key === 'listener_profile_rejected') ?? [],
    [systemMessages],
  );

  const submitAvatarGrade = () => {
    // When there is no pending proposed photo, the approve/reject endpoint has nothing
    // to act on. In that case, rejecting an already-approved photo must be done via a
    // direct user update that clears the approval flag instead.
    const hasProposedPhoto = !!peerData.proposed_profile_photo;

    if (!hasProposedPhoto) {
      const approved = selectedStatus === 'approved';
      updateUser.mutate(
        { userId: peerData.user.id, data: { listener_role: { profile_photo_approved: approved } } },
        {
          onSuccess: () => {
            savedStatusRef.current = selectedStatus;
            onDirtyChange?.(false);
            refetchPeerData();
            setMessagePopUp(false);
          },
          onError: (error: any) => {
            console.error('Profile update error:', error);
          },
        },
      );
      return;
    }

    const payload =
      selectedStatus === 'rejected'
        ? rejectionModalData.sendMessage && rejectionModalData.templateId
          ? {
              system_message_id: rejectionModalData.templateId,
              send_message: true,
              message: rejectionModalData.message,
              subject: '',
            }
          : { send_message: false }
        : undefined;

    updateListenerProfile(
      {
        listenerId: peerData.id,
        type: selectedStatus === 'approved' ? 'approve' : 'reject',
        payload: payload,
      },
      {
        onSuccess: () => {
          savedStatusRef.current = selectedStatus;
          onDirtyChange?.(false);
          refetchPeerData();
          setMessagePopUp(false);
        },
        onError: (error: any) => {
          console.error('Profile update error:', error);
        },
      },
    );
  };

  const handleSave = () => {
    if (selectedStatus === 'rejected') {
      const firstMsg = avatarRejectedMessages[0];
      setRejectionModalData({
        templateId: firstMsg?.id ?? null,
        message: firstMsg?.message ?? '',
        sendMessage: firstMsg != null,
      });
      setMessagePopUp(true);
    } else {
      submitAvatarGrade();
    }
  };

  const handleDownload = (url: string, filename: string, setLoading: (v: boolean) => void) => {
    setLoading(true);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Failed to download photo.');
          setLoading(false);
          return;
        }
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
          document.body.removeChild(link);
        }, 100);
        toast.success('Downloaded successfully');
        setLoading(false);
      }, 'image/jpeg');
    };
    img.onerror = async () => {
      // CDN blocked crossOrigin request — fetch with auth token as fallback
      try {
        const token = authToken || localStorage.getItem('authorization_token');
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error();
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
          document.body.removeChild(link);
        }, 100);
        toast.success('Downloaded successfully');
      } catch {
        toast.error('Failed to download photo.');
      } finally {
        setLoading(false);
      }
    };
    img.src = url;
  };

  const uploadPhoto = (imageBase64: string) => {
    updateUser.mutate(
      { userId: peerData.user.id, data: { listener_role: { profile_photo_file_data: imageBase64 } } },
      {
        onSuccess: () => {
          toast.success('Profile photo uploaded successfully!');
          setSelectedStatus('review');
          setTimeout(() => {
            refetchPeerData();
          }, 500);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
        onError: () => {
          toast.error('Failed to upload profile photo');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      },
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const validationError = validateImageFile(file);
    if (validationError) {
      toast.error(validationError);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setIsCropEditorOpen(true);
    };
    reader.readAsDataURL(file!);
  };

  const handleCropConfirm = async (pixels: Area) => {
    if (!imageToCrop) return;
    try {
      const cropped = await cropImageWithPixels(imageToCrop, pixels);
      setIsCropEditorOpen(false);
      setImageToCrop(null);
      uploadPhoto(cropped);
    } catch {
      toast.error('Failed to crop image');
    }
  };

  const handleCropCancel = () => {
    setIsCropEditorOpen(false);
    setImageToCrop(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {isCropEditorOpen && imageToCrop && (
        <ImageCropEditor imageUrl={imageToCrop} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />
      )}

      <Modal
        isModel={messagePopUp}
        InnerComponent={
          <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <h3 className="mb-4 text-lg font-bold text-gray-900">Reject Profile Photo</h3>

              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Rejection Message Template</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={rejectionModalData.templateId ?? 'none'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'none') {
                      setRejectionModalData((prev) => ({ ...prev, templateId: null, message: '', sendMessage: false }));
                    } else {
                      const id = Number(val);
                      const msg = avatarRejectedMessages.find((m: SystemMessage) => m.id === id);
                      setRejectionModalData((prev) => ({
                        ...prev,
                        templateId: id,
                        message: msg?.message ?? '',
                        sendMessage: true,
                      }));
                    }
                  }}
                >
                  <option value="none">Do NOT send message</option>
                  {avatarRejectedMessages.map((m: SystemMessage) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {rejectionModalData.sendMessage && (
                <div className="mb-6">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    className="h-32 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={rejectionModalData.message}
                    onChange={(e) => setRejectionModalData((prev) => ({ ...prev, message: e.target.value }))}
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setMessagePopUp(false)}
                >
                  Cancel
                </button>
                <button
                  disabled={isLoading}
                  className="bg-red rounded px-4 py-2 text-sm font-semibold text-white opacity-100 hover:opacity-90 disabled:opacity-50"
                  onClick={submitAvatarGrade}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        }
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <p className="mb-1.5 text-xs font-medium text-gray-700">Current</p>
          <div className="flex flex-col items-start gap-2">
            {currentPhotoUrlWithCache ? (
              <>
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-gray-200">
                  <img src={currentPhotoUrlWithCache} alt="Current profile" className="h-full w-full object-cover" />
                </div>
                <button
                  onClick={() => handleDownload(currentPhotoUrl!, 'current-photo.jpg', setIsDownloadingCurrent)}
                  disabled={isDownloadingCurrent}
                  className="rounded bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                >
                  {isDownloadingCurrent ? 'Downloading...' : 'Download'}
                </button>
              </>
            ) : (
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100">
                <p className="text-center text-[10px] leading-tight text-gray-500">No Photo</p>
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="mb-1.5 text-xs font-medium text-gray-700">Proposed</p>
          <div className="flex flex-col items-start gap-2">
            {proposedPhotoUrlWithCache ? (
              <>
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-gray-200">
                  <img src={proposedPhotoUrlWithCache} alt="Proposed profile" className="h-full w-full object-cover" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleDownload(proposedPhotoUrl!, 'proposed-photo.jpg', setIsDownloadingProposed)}
                    disabled={isDownloadingProposed}
                    className="rounded bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                  >
                    {isDownloadingProposed ? 'Downloading...' : 'Download'}
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={updateUser.isLoading}
                    className="rounded bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {updateUser.isLoading ? 'Uploading...' : 'Upload new'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100">
                  <p className="text-center text-[10px] leading-tight text-gray-500">No Photo</p>
                </div>
                <button
                  onClick={handleUpload}
                  disabled={updateUser.isLoading}
                  className="rounded bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  {updateUser.isLoading ? 'Uploading...' : 'Upload new'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-2 border-t border-gray-100 pt-3 lg:w-44 lg:border-t-0 lg:pt-0">
          <div>
            <p className="mb-1 text-xs font-medium text-gray-700">Profile Status</p>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
            >
              <option value="review">Needs Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <button
            onClick={handleSave}
            disabled={isLoading || selectedStatus === 'review'}
            className="rounded bg-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            {isLoading ? 'Saving...' : 'Save photo status'}
          </button>
        </div>
      </div>
    </>
  );
};

export default AvatarReview;
